import React, { useEffect, useState, useCallback } from "react";
import { db, auth } from "./firebase";
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./navbar-spacing.css";

const C = {
  bg:"#0a0a0f", surface:"rgba(255,255,255,0.04)", sh:"rgba(255,255,255,0.07)",
  bg2:"#111118", border:"rgba(255,255,255,0.08)", text:"#f5f5f7",
  sec:"rgba(245,245,247,0.55)", ter:"rgba(245,245,247,0.30)",
  accent:"#0a84ff", green:"#30d158", orange:"#ff9f0a", red:"#ff453a",
};
const F = "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif";
const inp = { width:"100%", padding:"0.68rem 0.85rem", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)", borderRadius:10, color:C.text, fontSize:"0.85rem", boxSizing:"border-box", outline:"none", fontFamily:F, marginBottom:"0.9rem" };
const Lbl = ({ children }) => <label style={{ display:"block", marginBottom:"0.3rem", fontSize:"0.7rem", color:C.sec, fontWeight:500 }}>{children}</label>;

export default function GestioneBanner() {
  const [banners, setBanners] = useState([]);
  const [localeTag, setLocaleTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [locali, setLocali] = useState([]);
  const [localiSelezionati, setLocaliSelezionati] = useState([]);
  const [nuovoTitolo, setNuovoTitolo] = useState("");
  const [nuovoLink, setNuovoLink] = useState("");
  const [nuovaImmagine, setNuovaImmagine] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [modalitaModifica, setModalitaModifica] = useState(false);
  const [bannerInModifica, setBannerInModifica] = useState(null);
  const navigate = useNavigate();

  const toast = (msg, delay=3000) => { setSnackbar(msg); setTimeout(()=>setSnackbar(""), delay); };

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async user => {
      if (!user) { navigate("/login"); return; }
      const snap = await getDoc(doc(db, "utenti", user.uid));
      if (snap.data()?.ruolo !== "admin") { navigate("/dashboard"); return; }
      setLocaleTag("admin");
    });
    return () => unsub();
  }, [navigate]);

  // ── Load locali ───────────────────────────────────────────────────────────
  useEffect(() => {
    getDocs(collection(db,"locali")).then(s=>setLocali(s.docs.map(d=>({id:d.id,nome:d.data().nome||d.id})))).catch(()=>{});
  }, []);

  // ── Load banners ──────────────────────────────────────────────────────────
  const ricaricaBanners = useCallback(async () => {
    if (!localeTag) return;
    setLoading(true);
    try {
      const s = await getDocs(collection(db,"banners"));
      setBanners(s.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.timestamp?.seconds||0)-(a.timestamp?.seconds||0)));
    } catch {}
    setLoading(false);
  }, [localeTag]);

  useEffect(() => { ricaricaBanners(); }, [ricaricaBanners]);

  // ── Carica banner ─────────────────────────────────────────────────────────
  const caricaBanner = async (e) => {
    e.preventDefault();
    if (!localeTag?.trim()) { toast("❌ Utente non autenticato."); return; }
    if (!nuovaImmagine || !nuovoTitolo.trim()) { toast("❌ Inserisci titolo e immagine"); return; }
    if (!localiSelezionati.length) { toast("❌ Seleziona almeno un locale"); return; }
    setUploading(true);
    try {
      const storage=getStorage(), ts=Date.now();
      const nomeFile=`banners/admin_${ts}_${nuovaImmagine.name}`;
      await uploadBytes(ref(storage,nomeFile), nuovaImmagine);
      const imageUrl=await getDownloadURL(ref(storage,nomeFile));
      await Promise.all(localiSelezionati.map(lid=>addDoc(collection(db,"banners"),{localeTag:lid.toLowerCase().replace(/[^a-z0-9]/gi,""),titolo:nuovoTitolo.trim(),link:nuovoLink.trim()||"",imageUrl,storagePath:nomeFile,timestamp:serverTimestamp(),attivo:true})));
      toast(`✅ Banner caricato per ${localiSelezionati.length} locale/i!`);
      setNuovoTitolo(""); setNuovoLink(""); setNuovaImmagine(null); setLocaliSelezionati([]);
      setFormKey(p=>p+1); setShowForm(false);
      await ricaricaBanners();
    } catch (err) {
      toast(err.code==="storage/unauthorized"?"❌ Permessi Storage mancanti":"❌ Errore nel caricamento");
    }
    setUploading(false);
  };

  // ── Elimina ───────────────────────────────────────────────────────────────
  const eliminaBanner = async (banner) => {
    if (!window.confirm(`Eliminare "${banner.titolo}"?`)) return;
    try {
      if (banner.storagePath) await deleteObject(ref(getStorage(),banner.storagePath)).catch(()=>{});
      await deleteDoc(doc(db,"banners",banner.id));
      setBanners(p=>p.filter(b=>b.id!==banner.id)); toast("✅ Banner eliminato");
    } catch { toast("❌ Errore eliminazione"); }
  };

  // ── Toggle ────────────────────────────────────────────────────────────────
  const toggleAttivo = async (banner) => {
    try {
      await updateDoc(doc(db,"banners",banner.id),{attivo:!banner.attivo});
      setBanners(p=>p.map(b=>b.id===banner.id?{...b,attivo:!b.attivo}:b));
      toast(`✅ Banner ${!banner.attivo?"attivato":"disattivato"}`);
    } catch { toast("❌ Errore aggiornamento"); }
  };

  // ── Modifica ──────────────────────────────────────────────────────────────
  const apriModifica = (banner) => {
    setBannerInModifica(banner); setNuovoTitolo(banner.titolo); setNuovoLink(banner.link);
    const stessa = banners.filter(b=>b.imageUrl===banner.imageUrl);
    setLocaliSelezionati(stessa.map(b=>b.localeTag));
    setModalitaModifica(true); setShowForm(true);
  };

  const chiudiModifica = () => {
    setModalitaModifica(false); setBannerInModifica(null); setNuovoTitolo(""); setNuovoLink(""); setNuovaImmagine(null); setLocaliSelezionati([]); setShowForm(false); setFormKey(p=>p+1);
  };

  const salvaModifiche = async () => {
    if (!nuovoTitolo.trim()) { toast("⚠️ Inserisci un titolo!"); return; }
    if (!localiSelezionati.length) { toast("⚠️ Seleziona almeno un locale!"); return; }
    setUploading(true);
    try {
      const stessa=banners.filter(b=>b.imageUrl===bannerInModifica.imageUrl);
      const attuali=stessa.map(b=>b.localeTag);
      const daAggiungere=localiSelezionati.filter(l=>!attuali.includes(l));
      const daRimuovere=attuali.filter(l=>!localiSelezionati.includes(l));
      for (const b of stessa) if (localiSelezionati.includes(b.localeTag)) await updateDoc(doc(db,"banners",b.id),{titolo:nuovoTitolo,link:nuovoLink});
      for (const lt of daAggiungere) await addDoc(collection(db,"banners"),{localeTag:lt,titolo:nuovoTitolo,link:nuovoLink,imageUrl:bannerInModifica.imageUrl,storagePath:bannerInModifica.storagePath,timestamp:new Date(),attivo:true});
      for (const lt of daRimuovere) { const b=stessa.find(x=>x.localeTag===lt); if (b) await deleteDoc(doc(db,"banners",b.id)); }
      if (nuovaImmagine) {
        const storage=getStorage(), ts=Date.now(), fp=`banners/admin_${ts}_${nuovaImmagine.name}`;
        await uploadBytes(ref(storage,fp),nuovaImmagine);
        const url=await getDownloadURL(ref(storage,fp));
        for (const lt of localiSelezionati) { const b=banners.find(x=>x.localeTag===lt&&x.imageUrl===bannerInModifica.imageUrl); if (b) await updateDoc(doc(db,"banners",b.id),{imageUrl:url,storagePath:fp}); }
        if (bannerInModifica.storagePath) await deleteObject(ref(storage,bannerInModifica.storagePath)).catch(()=>{});
      }
      toast("✅ Banner modificato!"); chiudiModifica(); await ricaricaBanners();
    } catch { toast("❌ Errore nella modifica"); }
    setUploading(false);
  };

  const toggleLocale = (id) => setLocaliSelezionati(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const selectAll = () => setLocaliSelezionati(locali.map(l=>l.id));
  const clearAll = () => setLocaliSelezionati([]);

  return (
    <>
      <Navbar />
      <div style={{ minHeight:"100vh", background:C.bg, fontFamily:F, color:C.text }}>
        <div className="page-with-navbar" style={{ maxWidth:960, margin:"0 auto", padding:"2rem 1.5rem 4rem" }}>

          {/* Header */}
          <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"space-between", alignItems:"flex-end", gap:"1rem", marginBottom:"1.75rem" }}>
            <div>
              <p style={{ margin:"0 0 0.25rem", fontSize:"0.72rem", color:C.sec, fontWeight:500 }}>Pubblicità</p>
              <h1 style={{ margin:0, fontSize:"clamp(1.4rem,3vw,1.9rem)", fontWeight:700, letterSpacing:"-0.5px" }}>Gestione Banner</h1>
            </div>
            <button onClick={()=>{ chiudiModifica(); setShowForm(f=>!f); }} style={{ padding:"0.62rem 1.2rem", background: showForm?"rgba(255,255,255,0.07)":C.accent, border:`1px solid ${showForm?C.border:C.accent}`, borderRadius:12, color: showForm?C.sec:"#fff", fontSize:"0.85rem", fontWeight:600, cursor:"pointer", fontFamily:F }}>
              {showForm ? "Chiudi" : "+ Nuovo banner"}
            </button>
          </div>

          {/* Toast */}
          {snackbar && (
            <div style={{ position:"fixed", bottom:"2rem", left:"50%", transform:"translateX(-50%)", padding:"0.8rem 1.5rem", borderRadius:12, background: snackbar.includes("✅")?"rgba(48,209,88,0.15)":"rgba(255,69,58,0.15)", border:`1px solid ${snackbar.includes("✅")?"rgba(48,209,88,0.3)":"rgba(255,69,58,0.3)"}`, color: snackbar.includes("✅")?C.green:C.red, fontSize:"0.82rem", fontWeight:600, zIndex:9999, backdropFilter:"blur(8px)", boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
              {snackbar.replace("✅ ","").replace("❌ ","").replace("⚠️ ","")}
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:20, padding:"1.5rem", marginBottom:"1.5rem" }}>
              <h2 style={{ margin:"0 0 1.25rem", fontSize:"1rem", fontWeight:700 }}>
                {modalitaModifica ? "Modifica banner" : "Nuovo banner"}
              </h2>
              <form key={formKey} onSubmit={modalitaModifica?(e)=>{e.preventDefault();salvaModifiche();}:caricaBanner}>

                {/* Locale picker */}
                <Lbl>Locali *</Lbl>
                <div style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", marginBottom:"0.9rem" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"0.65rem 0.9rem", borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:"0.75rem", color:C.sec }}>{localiSelezionati.length}/{locali.length} selezionati</span>
                    <div style={{ display:"flex", gap:"0.75rem" }}>
                      <button type="button" onClick={selectAll} style={{ background:"none",border:"none",color:C.accent,fontSize:"0.72rem",fontWeight:600,cursor:"pointer",fontFamily:F }}>Tutti</button>
                      <button type="button" onClick={clearAll} style={{ background:"none",border:"none",color:C.sec,fontSize:"0.72rem",cursor:"pointer",fontFamily:F }}>Nessuno</button>
                    </div>
                  </div>
                  <div style={{ maxHeight:200, overflowY:"auto" }}>
                    {locali.map((l,i)=>{
                      const sel=localiSelezionati.includes(l.id);
                      return (
                        <label key={l.id} style={{ display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.6rem 0.9rem", cursor:"pointer", borderBottom: i<locali.length-1?`1px solid ${C.border}`:"none", background: sel?"rgba(10,132,255,0.08)":"transparent", transition:"background 120ms ease" }}>
                          <input type="checkbox" checked={sel} onChange={()=>toggleLocale(l.id)} style={{ accentColor:C.accent, width:16, height:16 }} />
                          <span style={{ fontSize:"0.82rem", color:C.text }}>{l.nome}</span>
                          {sel && <span style={{ marginLeft:"auto", fontSize:"0.62rem", color:C.accent, fontWeight:700 }}>✓</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Lbl>Titolo *</Lbl>
                <input style={inp} type="text" value={nuovoTitolo} onChange={e=>setNuovoTitolo(e.target.value)} placeholder="Es. Offerta Speciale" required
                  onFocus={e=>e.target.style.borderColor=C.accent+"88"} onBlur={e=>e.target.style.borderColor="rgba(255,255,255,0.09)"} />

                <Lbl>Link (opzionale)</Lbl>
                <input style={inp} type="url" value={nuovoLink} onChange={e=>setNuovoLink(e.target.value)} placeholder="https://…"
                  onFocus={e=>e.target.style.borderColor=C.accent+"88"} onBlur={e=>e.target.style.borderColor="rgba(255,255,255,0.09)"} />

                <Lbl>Immagine {modalitaModifica?"(opzionale)":"*"}</Lbl>
                <input type="file" accept="image/*" onChange={e=>setNuovaImmagine(e.target.files[0])} required={!modalitaModifica}
                  style={{ ...inp, border:"1px dashed rgba(255,255,255,0.12)", cursor:"pointer" }} />

                <button type="submit" disabled={uploading||!localiSelezionati.length}
                  style={{ width:"100%", padding:"0.82rem", background: uploading||!localiSelezionati.length?"rgba(10,132,255,0.4)":C.accent, border:"none", borderRadius:12, color:"#fff", fontSize:"0.88rem", fontWeight:600, cursor: uploading||!localiSelezionati.length?"not-allowed":"pointer", fontFamily:F }}>
                  {uploading ? "Caricamento…" : modalitaModifica ? "Salva modifiche" : "Pubblica banner"}
                </button>
              </form>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem", padding:"3rem 0" }}>
              <div style={{ width:32, height:32, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.sec}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
              <p style={{ margin:0, color:C.sec, fontSize:"0.82rem" }}>Caricamento banner…</p>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : banners.length===0 ? (
            <div style={{ textAlign:"center", padding:"3rem", background:C.surface, border:`1px solid ${C.border}`, borderRadius:20 }}>
              <p style={{ margin:"0 0 0.5rem", fontSize:"1rem", color:C.sec }}>Nessun banner pubblicato</p>
              <p style={{ margin:0, fontSize:"0.8rem", color:C.ter }}>Clicca su "Nuovo banner" per iniziare</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:"0.75rem" }}>
              {banners.map(item=>(
                <div key={item.id} style={{ background:C.surface, border:`1px solid ${item.attivo?C.accent+"33":C.border}`, borderRadius:18, overflow:"hidden", opacity: item.attivo?1:0.65, transition:"border-color 200ms ease" }}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"1rem", padding:"1rem 1.1rem" }}>
                    <img src={item.imageUrl} alt={item.titolo} style={{ width:120, height:80, objectFit:"cover", borderRadius:10, flexShrink:0, border:`1px solid ${C.border}` }} />
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", marginBottom:"0.35rem" }}>
                        <p style={{ margin:0, fontSize:"0.92rem", fontWeight:700 }}>{item.titolo}</p>
                        <span style={{ fontSize:"0.62rem", fontWeight:700, padding:"2px 8px", borderRadius:6, background: item.attivo?"rgba(48,209,88,0.12)":"rgba(255,255,255,0.06)", color: item.attivo?C.green:C.ter, border:`1px solid ${item.attivo?"rgba(48,209,88,0.25)":C.border}` }}>
                          {item.attivo?"Attivo":"Inattivo"}
                        </span>
                      </div>
                      <p style={{ margin:"0 0 0.2rem", fontSize:"0.72rem", color:C.sec }}>{item.localeTag}</p>
                      {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ fontSize:"0.72rem", color:C.accent }}>{item.link}</a>}
                      <p style={{ margin:"0.3rem 0 0", fontSize:"0.65rem", color:C.ter }}>{item.timestamp?.seconds?new Date(item.timestamp.seconds*1000).toLocaleDateString("it-IT"):""}</p>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:"0.4rem", justifyContent:"center" }}>
                      {[{label:"Modifica",color:C.accent,fn:()=>apriModifica(item)},{label:item.attivo?"Disattiva":"Attiva",color:item.attivo?C.orange:C.green,fn:()=>toggleAttivo(item)},{label:"Elimina",color:C.red,fn:()=>eliminaBanner(item)}].map(({label,color,fn})=>(
                        <button key={label} onClick={fn} style={{ padding:"0.45rem 0.85rem", background:color+"15", border:`1px solid ${color}28`, borderRadius:9, color:C.text, fontSize:"0.75rem", fontWeight:600, cursor:"pointer", fontFamily:F, whiteSpace:"nowrap" }}>{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
